import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { Heart, AlertTriangle, TrendingDown, Shield } from 'lucide-react';
const symptoms = [{
  severity: 'Mild AMS',
  color: 'yellow',
  signs: ['Headache', 'Nausea or loss of appetite', 'Fatigue or weakness', 'Dizziness or lightheadedness', 'Difficulty sleeping']
}, {
  severity: 'Moderate AMS',
  color: 'orange',
  signs: ['Severe headache not relieved by medication', 'Vomiting', 'Increased weakness', 'Shortness of breath at rest', 'Decreased coordination']
}, {
  severity: 'Severe AMS (HACE/HAPE)',
  color: 'red',
  signs: ['Confusion or altered mental state', 'Inability to walk straight', 'Severe breathlessness at rest', 'Coughing up pink frothy fluid', 'Extreme fatigue']
}];
const preventionStrategies = [{
  icon: TrendingDown,
  title: 'Ascend Gradually',
  description: 'Follow the "climb high, sleep low" principle. Don\'t ascend more than 300-500m per day above 3,000m altitude.'
}, {
  icon: Heart,
  title: 'Stay Hydrated',
  description: 'Drink 3-4 liters of water daily. Dehydration worsens altitude sickness symptoms significantly.'
}, {
  icon: Shield,
  title: 'Take Rest Days',
  description: 'Schedule acclimatization days every 1,000m of elevation gain. Use these days for short hikes to higher elevations.'
}, {
  icon: AlertTriangle,
  title: 'Recognize Symptoms Early',
  description: 'Monitor yourself and teammates for symptoms. Early recognition and action prevent serious complications.'
}];
export function AltitudeSicknessPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-596">
      <Navbar data-id="element-597" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-598">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2070&auto=format&fit=crop")'
      }} data-id="element-599">
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/70 via-red-900/50 to-red-900/80" data-id="element-600" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-601">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-602">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-603">
              <Heart className="text-white" size={32} data-id="element-604" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-605">
            Altitude Sickness Prevention
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-red-100 font-light max-w-2xl mx-auto" data-id="element-606">
            Essential guide to recognizing, preventing, and treating altitude
            sickness
          </motion.p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-607">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-608">
          <SectionHeading title="Understanding Altitude Sickness" subtitle="What You Need to Know" data-id="element-609" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-610">
            <p data-id="element-611">
              Altitude sickness, also known as Acute Mountain Sickness (AMS),
              occurs when you ascend to high altitudes too quickly. At
              elevations above 2,500m (8,200ft), the air contains less oxygen,
              and your body needs time to adjust to these conditions.
            </p>
            <p data-id="element-612">
              Anyone can get altitude sickness, regardless of age, fitness
              level, or previous high-altitude experience. The key to prevention
              is proper acclimatization—giving your body time to adapt to
              decreasing oxygen levels as you climb higher.
            </p>
            <p data-id="element-613">
              Most cases of AMS are mild and resolve with rest and proper
              acclimatization. However, if ignored, altitude sickness can
              progress to life-threatening conditions like High Altitude
              Cerebral Edema (HACE) or High Altitude Pulmonary Edema (HAPE).
            </p>
          </div>
        </motion.div>
      </section>

      {/* Symptoms */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-614">
        <div className="max-w-7xl mx-auto" data-id="element-615">
          <SectionHeading title="Recognizing the Symptoms" subtitle="Know the warning signs" data-id="element-616" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-617">
            {symptoms.map((level, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`bg-white rounded-2xl p-8 shadow-lg border-t-4 ${level.color === 'yellow' ? 'border-yellow-500' : level.color === 'orange' ? 'border-orange-500' : 'border-red-500'}`} data-id="element-618">
                <h3 className={`text-xl font-bold mb-6 ${level.color === 'yellow' ? 'text-yellow-700' : level.color === 'orange' ? 'text-orange-700' : 'text-red-700'}`} data-id="element-619">
                  {level.severity}
                </h3>
                <ul className="space-y-3" data-id="element-620">
                  {level.signs.map((sign, signIndex) => <li key={signIndex} className="flex items-start gap-3" data-id="element-621">
                      <AlertTriangle className={`shrink-0 mt-0.5 ${level.color === 'yellow' ? 'text-yellow-500' : level.color === 'orange' ? 'text-orange-500' : 'text-red-500'}`} size={18} data-id="element-622" />
                      <span className="text-stone-700" data-id="element-623">{sign}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Prevention Strategies */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-id="element-624">
        <SectionHeading title="Prevention Strategies" subtitle="How to avoid altitude sickness" data-id="element-625" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-626">
          {preventionStrategies.map((strategy, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8" data-id="element-627">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6" data-id="element-628">
                <strategy.icon size={28} className="text-white" data-id="element-629" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3" data-id="element-630">
                {strategy.title}
              </h3>
              <p className="text-stone-700 leading-relaxed" data-id="element-631">
                {strategy.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* Treatment Protocol */}
      <section className="bg-red-50 py-20 px-4" data-id="element-632">
        <div className="max-w-4xl mx-auto" data-id="element-633">
          <SectionHeading title="What to Do If Symptoms Appear" subtitle="Treatment protocol" data-id="element-634" />
          <div className="bg-white rounded-2xl p-8 md:p-12 border-l-4 border-red-600" data-id="element-635">
            <div className="space-y-6" data-id="element-636">
              <div data-id="element-637">
                <h3 className="text-xl font-bold text-red-900 mb-3" data-id="element-638">
                  Immediate Actions
                </h3>
                <ul className="space-y-2 text-stone-700" data-id="element-639">
                  <li className="flex items-start gap-3" data-id="element-640">
                    <span className="font-bold text-red-600" data-id="element-641">1.</span>
                    <span data-id="element-642">
                      Stop ascending immediately. Do not climb higher with
                      symptoms.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-643">
                    <span className="font-bold text-red-600" data-id="element-644">2.</span>
                    <span data-id="element-645">
                      Rest at your current altitude for 24-48 hours until
                      symptoms improve.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-646">
                    <span className="font-bold text-red-600" data-id="element-647">3.</span>
                    <span data-id="element-648">
                      Stay hydrated and avoid alcohol and sleeping pills.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-649">
                    <span className="font-bold text-red-600" data-id="element-650">4.</span>
                    <span data-id="element-651">
                      Take pain relievers for headaches (ibuprofen or
                      paracetamol).
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-red-100 rounded-xl p-6" data-id="element-652">
                <h3 className="text-xl font-bold text-red-900 mb-3" data-id="element-653">
                  When to Descend
                </h3>
                <p className="text-red-800 leading-relaxed mb-3" data-id="element-654">
                  If symptoms worsen or don't improve after 24 hours of rest,
                  descend immediately. Descent is the most effective treatment
                  for altitude sickness.
                </p>
                <p className="text-red-800 leading-relaxed font-semibold" data-id="element-655">
                  For severe symptoms (HACE/HAPE): Descend immediately and seek
                  medical help. This is a medical emergency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medications */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-656">
        <SectionHeading title="Medications" subtitle="Consult your doctor before the trek" data-id="element-657" />
        <div className="bg-blue-50 rounded-2xl p-8" data-id="element-658">
          <div className="space-y-6" data-id="element-659">
            <div data-id="element-660">
              <h3 className="text-lg font-bold text-stone-900 mb-2" data-id="element-661">
                Diamox (Acetazolamide)
              </h3>
              <p className="text-stone-700 leading-relaxed" data-id="element-662">
                Helps prevent and reduce symptoms of AMS. Typically taken
                starting 1-2 days before ascent. Consult your doctor for proper
                dosage and to check for contraindications.
              </p>
            </div>
            <div data-id="element-663">
              <h3 className="text-lg font-bold text-stone-900 mb-2" data-id="element-664">
                Dexamethasone
              </h3>
              <p className="text-stone-700 leading-relaxed" data-id="element-665">
                Used for treating severe AMS, HACE, and HAPE. Should only be
                used under medical supervision or in emergency situations.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-blue-600" data-id="element-666">
              <p className="text-stone-700 leading-relaxed" data-id="element-667">
                <strong data-id="element-668">Important:</strong> Medications are not a substitute for
                proper acclimatization. They should be used as a supplement to,
                not a replacement for, gradual ascent and rest days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white" data-id="element-669">
        <div className="max-w-4xl mx-auto text-center" data-id="element-670">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-671">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-672">
              Trek Safely with Expert Guidance
            </h2>
            <p className="text-xl text-blue-100 mb-8" data-id="element-673">
              Our experienced guides are trained in altitude sickness
              recognition and emergency response
            </p>
            <a href="#plan-trip" data-id="element-674">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-colors" data-id="element-675">
                Plan Your Trek
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-676" />
    </div>;
}